import { useCallback, useEffect, useRef, useState } from 'react'

const START_SPEED = 8 // px / frame at first
const MAX_SPEED = 60 // px / frame eventually (capped lower so virtualized lists keep up)
const ACCEL_PER_FRAME = 0.8

export function ScrollAffordances() {
  const [showUp, setShowUp] = useState(false)
  const [showDown, setShowDown] = useState(false)
  const [activeDir, setActiveDir] = useState<1 | -1 | 0>(0)
  const activeDirRef = useRef<1 | -1 | 0>(0)
  activeDirRef.current = activeDir

  useEffect(() => {
    let raf = 0
    const update = () => {
      const winH = window.innerHeight
      const docH = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight,
      )
      const y = window.scrollY
      const overflow = docH - winH
      // While a button is held, never hide the matching arrow — otherwise
      // pointer-events: none drops it from under the cursor and the user
      // has to re-click. Hide only when truly idle.
      const upWanted = y > winH * 0.5
      const downWanted = overflow > 200 && y < overflow - 100
      setShowUp(upWanted || activeDirRef.current === -1)
      setShowDown(downWanted || activeDirRef.current === 1)
    }
    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        update()
      })
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', update)
    const ro = new ResizeObserver(update)
    ro.observe(document.body)
    return () => {
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', update)
      ro.disconnect()
    }
  }, [])

  // Press-and-hold continuous scroll
  const animRef = useRef<number | null>(null)
  const dirRef = useRef<0 | 1 | -1>(0)
  const speedRef = useRef(0)

  const stop = useCallback(() => {
    dirRef.current = 0
    speedRef.current = 0
    if (animRef.current) {
      cancelAnimationFrame(animRef.current)
      animRef.current = null
    }
    setActiveDir(0)
  }, [])

  const start = useCallback((dir: 1 | -1) => {
    dirRef.current = dir
    speedRef.current = START_SPEED
    setActiveDir(dir)
    let stallFrames = 0
    const tick = () => {
      if (dirRef.current === 0) {
        animRef.current = null
        return
      }
      const yBefore = window.scrollY
      window.scrollBy({ top: dirRef.current * speedRef.current, behavior: 'auto' })
      speedRef.current = Math.min(MAX_SPEED, speedRef.current + ACCEL_PER_FRAME)
      const docH = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight,
      )
      const winH = window.innerHeight
      const yAfter = window.scrollY
      const moved = Math.abs(yAfter - yBefore) > 0.5

      if (!moved) {
        stallFrames += 1
        // Stalling near bottom of virtualized list — wait a few frames for
        // more rows to render in. Give up after ~30 frames (~500ms).
        if (
          (dirRef.current === 1 && yAfter >= docH - winH - 1) ||
          (dirRef.current === -1 && yAfter <= 0)
        ) {
          if (stallFrames > 30) {
            animRef.current = null
            return
          }
        }
      } else {
        stallFrames = 0
      }

      animRef.current = requestAnimationFrame(tick)
    }
    if (animRef.current) cancelAnimationFrame(animRef.current)
    animRef.current = requestAnimationFrame(tick)
  }, [])

  // global pointer/mouse up safety
  useEffect(() => {
    const onUp = () => stop()
    const onCancel = () => stop()
    window.addEventListener('mouseup', onUp)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onCancel)
    window.addEventListener('blur', onCancel)
    return () => {
      window.removeEventListener('mouseup', onUp)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onCancel)
      window.removeEventListener('blur', onCancel)
    }
  }, [stop])

  const jumpTo = (dir: 1 | -1) => {
    const docH = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight,
    )
    window.scrollTo({ top: dir === 1 ? docH : 0, behavior: 'smooth' })
  }

  function buttonProps(dir: 1 | -1, label: string) {
    return {
      type: 'button' as const,
      'aria-label': label,
      title: `${label} — hold to scroll continuously, double-click to jump`,
      onPointerDown: (e: React.PointerEvent<HTMLButtonElement>) => {
        e.preventDefault()
        // Capture the pointer so mouseleave / scrolling away doesn't
        // cancel the hold mid-scroll.
        try {
          ;(e.currentTarget as HTMLButtonElement).setPointerCapture(e.pointerId)
        } catch {
          /* ignore */
        }
        start(dir)
      },
      onPointerUp: (e: React.PointerEvent<HTMLButtonElement>) => {
        try {
          ;(e.currentTarget as HTMLButtonElement).releasePointerCapture(e.pointerId)
        } catch {
          /* ignore */
        }
        stop()
      },
      onPointerCancel: () => stop(),
      onDoubleClick: () => jumpTo(dir),
      onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
    }
  }

  const baseClass =
    'select-none touch-none grid h-11 w-11 place-items-center rounded-full border border-white/10 bg-ink-900/70 text-lg text-white backdrop-blur-md shadow-glass transition'

  return (
    <div className="fixed bottom-5 left-5 z-[90] flex flex-col gap-2">
      <button
        {...buttonProps(-1, 'scroll up')}
        className={`${baseClass} hover:border-[color:var(--accent-strong)] hover:text-[color:var(--accent)] ${
          showUp ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-2 opacity-0'
        } ${activeDir === -1 ? 'border-[color:var(--accent-strong)] text-[color:var(--accent)]' : ''}`}
      >
        ↑
      </button>
      <button
        {...buttonProps(1, 'scroll down')}
        className={`${baseClass} hover:border-[color:var(--accent-strong)] hover:text-[color:var(--accent)] ${
          showDown ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-2 opacity-0'
        } ${activeDir === 1 ? 'border-[color:var(--accent-strong)] text-[color:var(--accent)]' : ''}`}
      >
        ↓
      </button>
    </div>
  )
}
